/** The Sandbox tab's "State" block: one row per thing that can stop or limit a sandbox, each with its
 *  state in words, what it means, and the action that changes it (`console_sandbox_manage`):
 *
 *  - Suspension (F2; SBX D6) — the kill switch. An ops suspension is locked: the sandbox's own admins
 *    see it but cannot clear it; suspending over their own suspension locks it.
 *  - Inbound — the circuit breaker (S6a; SBX D4 §4, SBX-cb): tripped, since when, which threshold, and
 *    the requests bproxy holds; the reset delivers or drops them.
 *  - Booking quota — "lane paused" (S6a; SBX D4 §3, O29): a reached quota pauses only this sandbox's
 *    lane, until the daily reset or until ops raise it (Quotas, below).
 *  - Ingress (S6a; M1) — log | enforce: whether requests that skipped the sandbox ingress host are
 *    refused (421), with the last 7 days' count to check before enforcing.
 *  - Production key (S3; SBX D3, O16) — whether its vendor credentials reach production data.
 *  - Scheduled pull (S6c; SBX D7 §8, O29) — read-only: whether production's crons pull for the
 *    sandbox (paused with its lane), the capped cadence, per restech account whether its next pull
 *    runs (production key, priority class, a vendor-429 backoff), and the shared-vendor-quota warning.
 *
 *  Every write is audited server-side; each dialog takes an optional comment for that record. */
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import InventoryOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import LoginIcon from '@mui/icons-material/Login';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  sandboxApi,
  sandboxErrorMessage,
  sandboxKeys,
  type BreakerAction,
  type IngressMode,
  type SandboxPullBackoff,
  type SandboxState,
} from '@/api/sandbox';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';

import {
  BREAKER_ACTION_LABEL,
  INGRESS_MODE_LABEL,
  INGRESS_REASON_LABEL,
  PULL_PAUSED_LABEL,
  QUOTA_LABEL,
  SCHEDULED_PULL_LABEL,
  breakerReasonText,
  breakerThresholdsText,
  pullCadenceText,
  suspendedByText,
  throttleText,
} from './sandboxWords';
import { When } from './When';

export type ChipTone = 'success' | 'warning' | 'default' | 'info' | 'error';

export function StateChip({ tone, label }: { tone: ChipTone; label: string }) {
  return <Chip size="small" variant="outlined" color={tone} label={label} sx={{ flex: 'none' }} />;
}

/** One row: label column, the state + explanation, and the actions — stacked on a narrow page. */
export function StateRow({
  icon,
  label,
  chip,
  children,
  actions,
}: {
  icon: ReactNode;
  label: string;
  chip: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Box
      role="group"
      aria-label={label}
      data-state-row={label}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '170px minmax(0,1fr) auto' },
        columnGap: 2,
        rowGap: 0.75,
        alignItems: 'start',
        py: 1.25,
        '& + &': { borderTop: 1, borderColor: 'divider' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 24 }}>
        <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box>
        <Typography variant="subtitle2" component="h3">
          {label}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ mb: 0.5 }}>{chip}</Box>
        <Typography
          variant="body2"
          color="text.secondary"
          component="div"
          sx={{ maxWidth: '80ch' }}
        >
          {children}
        </Typography>
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifySelf: { md: 'end' } }}>
          {actions}
        </Box>
      )}
    </Box>
  );
}

function useInvalidateSandbox(topId: number) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['tourOperators', 'sandbox', topId] });
}

export function SandboxStateSection({
  topId,
  state,
  canManage,
}: {
  topId: number;
  state: SandboxState;
  canManage: boolean;
}) {
  return (
    <Box>
      <SuspensionRow topId={topId} state={state} canManage={canManage} />
      <BreakerRow topId={topId} state={state} canManage={canManage} />
      <QuotaRow state={state} />
      <IngressRow topId={topId} state={state} canManage={canManage} />
      <ProductionKeyRow state={state} />
      <PullRow state={state} />
    </Box>
  );
}

// --------------------------------------------------------------------------- suspension

function SuspensionRow({
  topId,
  state,
  canManage,
}: {
  topId: number;
  state: SandboxState;
  canManage: boolean;
}) {
  const s = state.suspension;
  const [dialog, setDialog] = useState<'suspend' | 'resume' | null>(null);
  if (!s) return null;
  const chip = s.suspended ? (
    <StateChip
      tone="warning"
      label={s.locked ? `${suspendedByText(s)} — locked` : suspendedByText(s)}
    />
  ) : (
    <StateChip tone="success" label="Running" />
  );
  return (
    <>
      <StateRow
        icon={<PowerSettingsNewIcon fontSize="small" />}
        label="Suspension"
        chip={chip}
        actions={
          canManage && (
            <>
              {(!s.suspended || !s.locked) && (
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => setDialog('suspend')}
                >
                  {s.suspended ? 'Suspend and lock…' : 'Suspend…'}
                </Button>
              )}
              {s.suspended && (
                <Button size="small" variant="outlined" onClick={() => setDialog('resume')}>
                  Resume…
                </Button>
              )}
            </>
          )
        }
      >
        {s.suspended ? (
          <>
            Every operation is stopped: API writes are answered 423, its tasks are dropped, its
            sends refused and crons skip it. Reads stay allowed. Since <When iso={s.at} />
            {s.reason ? `: ${s.reason}` : ''}.{' '}
            {s.locked
              ? 'Locked: the sandbox’s own admins cannot resume it.'
              : 'Its developers suspended it themselves; suspending it here locks it.'}
          </>
        ) : (
          'Suspending stops every operation of this sandbox at once — API writes, tasks, sends and crons — and keeps reads allowed. An ops suspension is locked.'
        )}
      </StateRow>
      <SuspensionDialog topId={topId} mode={dialog} onClose={() => setDialog(null)} />
    </>
  );
}

interface SuspendValues {
  reason: string;
}

function SuspensionDialog({
  topId,
  mode,
  onClose,
}: {
  topId: number;
  mode: 'suspend' | 'resume' | null;
  onClose: () => void;
}) {
  const snackbar = useSnackbar();
  const invalidate = useInvalidateSandbox(topId);
  const { register, handleSubmit, reset } = useForm<SuspendValues>({
    defaultValues: { reason: '' },
  });
  const save = useMutation({
    mutationFn: (v: SuspendValues) =>
      sandboxApi.setSuspension(
        topId,
        mode === 'suspend' ? { suspended: true, reason: v.reason.trim() } : { suspended: false },
      ),
    onSuccess: (after) => {
      snackbar.show(
        after.suspended ? 'Sandbox suspended (locked).' : 'Sandbox resumed.',
        'success',
      );
      void invalidate();
      reset();
      onClose();
    },
    onError: (e) => snackbar.show(sandboxErrorMessage(e), 'error'),
  });
  const suspending = mode === 'suspend';
  return (
    <AppDialog
      open={mode !== null}
      onClose={onClose}
      title={suspending ? 'Suspend the sandbox' : 'Resume the sandbox'}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color={suspending ? 'error' : 'primary'}
            disabled={save.isPending}
            onClick={handleSubmit((v) => save.mutate(v))}
          >
            {suspending ? 'Suspend' : 'Resume'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2">
          {suspending
            ? 'Every operation of the sandbox stops within seconds: API writes get 423, queued tasks are dropped, sends are refused, crons skip it. Reads stay allowed. The suspension is locked: its own admins cannot resume it.'
            : 'The sandbox runs again: crons pick it up on their next tick. Tasks dropped while it was suspended are not replayed; its periodic syncs catch up.'}
        </Typography>
        {suspending && (
          <TextField
            label="Reason"
            size="small"
            inputProps={{ maxLength: 200 }}
            helperText="Shown to the sandbox’s developers and kept in the audit record."
            {...register('reason')}
          />
        )}
      </Stack>
    </AppDialog>
  );
}

// --------------------------------------------------------------------------- breaker

function BreakerRow({
  topId,
  state,
  canManage,
}: {
  topId: number;
  state: SandboxState;
  canManage: boolean;
}) {
  const b = state.breaker;
  const [open, setOpen] = useState(false);
  // The held count lives in bproxy's database: asked for separately, on this row only.
  const detail = useQuery({
    queryKey: sandboxKeys.breaker(topId),
    queryFn: () => sandboxApi.breaker(topId),
    enabled: Boolean(b),
  });
  if (!b) return null;
  const held = detail.data?.held;
  const heldText =
    held == null
      ? detail.isLoading
        ? ''
        : ' The held requests cannot be counted now (bproxy’s database is unreachable).'
      : ` ${held} request${held === 1 ? '' : 's'} held.`;
  // A reset is also how held requests left behind by a failed release get out.
  const canReset = canManage && (b.tripped || (held ?? 0) > 0);
  return (
    <>
      <StateRow
        icon={<CallReceivedIcon fontSize="small" />}
        label="Inbound"
        chip={
          b.tripped ? (
            <StateChip tone="warning" label="Paused — breaker tripped" />
          ) : (
            <StateChip tone="success" label="Delivering" />
          )
        }
        actions={
          canReset && (
            <Button
              size="small"
              variant={b.tripped ? 'contained' : 'outlined'}
              onClick={() => setOpen(true)}
            >
              {b.tripped ? 'Reset breaker…' : 'Release held requests…'}
            </Button>
          )
        }
      >
        {b.tripped ? (
          <>
            Tripped <When iso={b.since} />: {breakerReasonText(b)}. bproxy stores the sandbox’s new
            requests and delivers nothing until a reset.{heldText}
          </>
        ) : (
          <>
            {breakerThresholdsText(b)}
            {b.reset_at && (
              <>
                {' '}
                Last reset <When iso={b.reset_at} />
                {b.reset_by ? ` by ${b.reset_by}` : ''}
                {b.reset_action
                  ? ` (held requests ${b.reset_action === 'deliver' ? 'delivered' : 'dropped'})`
                  : ''}
                .
              </>
            )}
            {(held ?? 0) > 0 && heldText}
          </>
        )}
      </StateRow>
      <BreakerResetDialog
        topId={topId}
        open={open}
        held={held}
        tripped={b.tripped}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

interface BreakerValues {
  action: BreakerAction;
  comment: string;
}

function BreakerResetDialog({
  topId,
  open,
  held,
  tripped,
  onClose,
}: {
  topId: number;
  open: boolean;
  held: number | null | undefined;
  tripped: boolean;
  onClose: () => void;
}) {
  const snackbar = useSnackbar();
  const invalidate = useInvalidateSandbox(topId);
  const { control, register, handleSubmit, reset } = useForm<BreakerValues>({
    defaultValues: { action: 'deliver', comment: '' },
  });
  const save = useMutation({
    mutationFn: (v: BreakerValues) =>
      sandboxApi.resetBreaker(topId, { action: v.action, comment: v.comment.trim() || undefined }),
    onSuccess: (r) => {
      if (r.held < 0) {
        snackbar.show(
          'The breaker is reset, but bproxy’s held requests could not be released — repeat the reset.',
          'warning',
        );
      } else {
        snackbar.show(
          `Breaker reset: ${r.held} held request${r.held === 1 ? '' : 's'} ${r.action === 'deliver' ? 'delivered' : 'dropped'}.`,
          'success',
        );
      }
      void invalidate();
      reset();
      onClose();
    },
    onError: (e) => snackbar.show(sandboxErrorMessage(e, 'Reset failed.'), 'error'),
  });
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={tripped ? 'Reset the circuit breaker' : 'Release the held requests'}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={save.isPending}
            onClick={handleSubmit((v) => save.mutate(v))}
          >
            {tripped ? 'Reset' : 'Release'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2">
          {tripped
            ? 'The sandbox’s inbound resumes. What bproxy held while it was paused'
            : 'What bproxy still holds for the sandbox'}
          {held != null ? ` (${held} request${held === 1 ? '' : 's'})` : ''} is delivered, or
          dropped:
        </Typography>
        <Controller
          name="action"
          control={control}
          render={({ field }) => (
            <RadioGroup {...field} aria-label="What happens to the held requests">
              {(['deliver', 'drop'] as const).map((a) => (
                <FormControlLabel
                  key={a}
                  value={a}
                  control={<Radio size="small" />}
                  label={BREAKER_ACTION_LABEL[a]}
                />
              ))}
            </RadioGroup>
          )}
        />
        <TextField
          label="Comment"
          size="small"
          inputProps={{ maxLength: 250 }}
          {...register('comment')}
        />
      </Stack>
    </AppDialog>
  );
}

// --------------------------------------------------------------------------- quota (lane)

function QuotaRow({ state }: { state: SandboxState }) {
  const t = state.throttle;
  const eff = state.quotas?.effective;
  return (
    <StateRow
      icon={<InventoryOutlinedIcon fontSize="small" />}
      label="Booking quota"
      chip={
        t ? (
          <StateChip tone="warning" label="Lane paused" />
        ) : (
          <StateChip tone="success" label="Within quota" />
        )
      }
    >
      {t ? (
        <>
          {throttleText(t)}. New requests are answered 429 quota_exceeded; scheduled pulls are
          skipped. Only this sandbox’s lane pauses — production is not affected. Raise the quota
          under Quotas below to lift it.
        </>
      ) : eff ? (
        <>
          In force: {QUOTA_LABEL.bookings_per_day.toLowerCase()}{' '}
          {eff.bookings_per_day?.toLocaleString()}, {QUOTA_LABEL.bookings_total.toLowerCase()}{' '}
          {eff.bookings_total?.toLocaleString()}. Reaching one pauses only this sandbox’s lane.
        </>
      ) : (
        'Reaching a booking quota pauses only this sandbox’s lane.'
      )}
    </StateRow>
  );
}

// --------------------------------------------------------------------------- ingress

function IngressRow({
  topId,
  state,
  canManage,
}: {
  topId: number;
  state: SandboxState;
  canManage: boolean;
}) {
  const ing = state.ingress;
  const [target, setTarget] = useState<IngressMode | null>(null);
  if (!ing) return null;
  const days = Object.entries(ing.direct.days);
  const total = days.reduce((n, [, v]) => n + v, 0);
  const last = ing.direct.last;
  const other: IngressMode = ing.mode === 'enforce' ? 'log' : 'enforce';
  return (
    <>
      <StateRow
        icon={<LoginIcon fontSize="small" />}
        label="Ingress"
        chip={
          <StateChip
            tone={ing.mode === 'enforce' ? 'info' : 'default'}
            label={INGRESS_MODE_LABEL[ing.mode]}
          />
        }
        actions={
          canManage && (
            <Button size="small" variant="outlined" onClick={() => setTarget(other)}>
              {other === 'enforce' ? 'Enforce…' : 'Back to log only…'}
            </Button>
          )
        }
      >
        Requests must come through{' '}
        <ValueActions actions={[act.copy(ing.ingress_base_url, 'Copy the ingress base URL')]}>
          <Box component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {ing.ingress_base_url}
          </Box>
        </ValueActions>
        .{' '}
        {ing.mode === 'enforce'
          ? 'Those that skip it are refused (421).'
          : 'Those that skip it are only recorded.'}{' '}
        {total === 0
          ? 'None skipped it in the last 7 days.'
          : `${total} skipped it in the last 7 days`}
        {last && (
          <>
            {' '}
            (last <When iso={last.at} />: {last.method} {last.path}
            {last.reason ? `, ${INGRESS_REASON_LABEL[last.reason] ?? last.reason}` : ''})
          </>
        )}
        {total > 0 && '.'}
      </StateRow>
      <IngressDialog
        topId={topId}
        target={target}
        skipped={total}
        onClose={() => setTarget(null)}
      />
    </>
  );
}

function IngressDialog({
  topId,
  target,
  skipped,
  onClose,
}: {
  topId: number;
  target: IngressMode | null;
  skipped: number;
  onClose: () => void;
}) {
  const snackbar = useSnackbar();
  const invalidate = useInvalidateSandbox(topId);
  const { register, handleSubmit, reset } = useForm<{ comment: string }>({
    defaultValues: { comment: '' },
  });
  const save = useMutation({
    mutationFn: (v: { comment: string }) =>
      sandboxApi.setIngress(topId, {
        mode: target ?? 'log',
        comment: v.comment.trim() || undefined,
      }),
    onSuccess: (r) => {
      snackbar.show(`Ingress: ${INGRESS_MODE_LABEL[r.mode].toLowerCase()}.`, 'success');
      void invalidate();
      reset();
      onClose();
    },
    onError: (e) => snackbar.show(sandboxErrorMessage(e), 'error'),
  });
  const enforcing = target === 'enforce';
  return (
    <AppDialog
      open={target !== null}
      onClose={onClose}
      title={enforcing ? 'Enforce the sandbox ingress' : 'Back to log only'}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={save.isPending}
            onClick={handleSubmit((v) => save.mutate(v))}
          >
            {enforcing ? 'Enforce' : 'Log only'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2">
          {enforcing
            ? 'Requests to this sandbox that do not come through its ingress host will be refused with 421. Switch only once its developers were told (Developer notifications, docs changelog).'
            : 'Requests that skip the sandbox ingress host are accepted again and only recorded.'}
        </Typography>
        {enforcing && skipped > 0 && (
          <Alert severity="warning">
            {skipped} request{skipped === 1 ? '' : 's'} skipped the ingress host in the last 7 days:
            they would be refused.
          </Alert>
        )}
        <TextField
          label="Comment"
          size="small"
          inputProps={{ maxLength: 250 }}
          {...register('comment')}
        />
      </Stack>
    </AppDialog>
  );
}

// --------------------------------------------------------------------------- production key

function ProductionKeyRow({ state }: { state: SandboxState }) {
  const matches = state.production_key ?? [];
  const disabled = state.credentials_disabled ?? [];
  const chip =
    disabled.length > 0 ? (
      <StateChip tone="warning" label="Credentials disabled" />
    ) : matches.length > 0 ? (
      <StateChip tone="warning" label="Production key" />
    ) : (
      <StateChip tone="success" label="Test credentials" />
    );
  return (
    <StateRow icon={<KeyOutlinedIcon fontSize="small" />} label="Vendor key" chip={chip}>
      {matches.length === 0 &&
        disabled.length === 0 &&
        'No vendor credential of this sandbox matches a production key.'}
      {matches.map((m, i) => (
        <Box key={`m${i}`}>
          {m.restech}
          {m.account ? ` (${m.account})` : ''} matches {m.matches}’s key
          {m.matches_account ? ` (${m.matches_account})` : ''}
          {m.via === 'vendor_account'
            ? ' — the same vendor account'
            : m.via === 'unregistered'
              ? ' — no identity check exists for this system'
              : ''}
          {m.since && (
            <>
              , since <When iso={m.since} />
            </>
          )}
          .
        </Box>
      ))}
      {disabled.map((d, i) => (
        <Box key={`d${i}`}>
          {d.restech}
          {d.account ? ` (${d.account})` : ''}: {(d.fields ?? []).join(', ')} removed — they reach
          production
          {d.matches ? ` (${d.matches})` : ''} while the production-data gate is closed.
        </Box>
      ))}
      {matches.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          While it matches, allow-all is impossible and real guests’ sends stay in the outbox or go
          to the allowlist only.
        </Box>
      )}
      <Box sx={{ mt: 0.5 }}>
        Production-data gate:{' '}
        {state.production_data_gate_open
          ? 'open — a production key is allowed and warned.'
          : 'closed — a production key is refused.'}
      </Box>
    </StateRow>
  );
}

// --------------------------------------------------------------------------- scheduled pull

/** Read-only: the backend decides (S6c `pull.admit`); ops change it through the rows above —
 *  the quota, the breaker, the vendor key. */
function PullRow({ state }: { state: SandboxState }) {
  const p = state.pull;
  if (!p || p.environment !== 'sandbox') return null;
  // Crons skip a suspended sandbox (`operational()`); the pull block does not say so itself.
  const suspended = Boolean(state.suspension?.suspended);
  const restechs = p.restechs ?? [];
  const skipped = restechs.filter((r) => r.scheduled_pull !== 'allowed').length;
  const cadence = pullCadenceText(p.min_interval_minutes);
  const chip = suspended ? (
    <StateChip tone="warning" label="Stopped — sandbox suspended" />
  ) : p.paused ? (
    <StateChip
      tone={p.paused_reason === 'pulls_off' ? 'default' : 'warning'}
      label={(p.paused_reason && PULL_PAUSED_LABEL[p.paused_reason]) || 'Paused'}
    />
  ) : skipped > 0 ? (
    <StateChip
      tone="warning"
      label={`Running — ${skipped} account${skipped === 1 ? '' : 's'} skipped`}
    />
  ) : (
    <StateChip tone="success" label="Running" />
  );
  return (
    <StateRow icon={<ScheduleOutlinedIcon fontSize="small" />} label="Scheduled pull" chip={chip}>
      {suspended && (
        <Box>No scheduled pull runs while the sandbox is suspended (Suspension, above).</Box>
      )}
      {p.paused && p.paused_reason === 'pulls_off' && (
        <Box>Off: sandboxes don’t pull; bookings come from the mirror or a manual sync.</Box>
      )}
      {p.paused && p.paused_reason !== 'pulls_off' && (
        <Box>
          {p.paused_reason === 'quota_paused' && p.quota
            ? `Every scheduled pull is skipped — ${throttleText(p.quota)}.`
            : p.paused_reason === 'breaker_paused'
              ? 'Every scheduled pull is skipped while the circuit breaker is tripped (Inbound, above).'
              : p.paused_reason === 'state_unavailable'
                ? 'Every scheduled pull is skipped: its pull state cannot be read.'
                : 'Every scheduled pull is skipped.'}{' '}
          Production’s pulls of the same vendor accounts are not affected.
        </Box>
      )}
      {cadence && <Box>Capped cadence, per account: {cadence}.</Box>}
      {restechs.length === 0 && <Box>The sandbox holds no vendor account.</Box>}
      {restechs.map((r) => (
        <Box key={`${r.restech}:${r.account}`}>
          {r.restech}
          {r.account ? ` (${r.account})` : ''}
          {r.production_key ? ', production’s key' : ''}:{' '}
          {suspended && r.scheduled_pull === 'allowed'
            ? 'skipped — sandbox suspended'
            : (SCHEDULED_PULL_LABEL[r.scheduled_pull] ?? r.scheduled_pull)}
          {r.backoff && <BackoffText b={r.backoff} />}.
        </Box>
      ))}
      {(p.shared_vendor_quota ?? []).map((w, i) => (
        <Box key={`w${i}`} sx={{ mt: 0.5 }}>
          {w.text}
        </Box>
      ))}
    </StateRow>
  );
}

/** " — answered 429 3 times in a row (last 2m ago, Retry-After 120 s); pulls resume in 4m" */
function BackoffText({ b }: { b: SandboxPullBackoff }) {
  const streak = b.streak ?? 1;
  return (
    <>
      {' '}
      — answered {b.status ?? 429} {streak === 1 ? 'once' : `${streak} times in a row`} (last{' '}
      <When iso={b.at} />
      {b.retry_after != null ? `, Retry-After ${b.retry_after} s` : ''}); pulls resume{' '}
      <When iso={b.until} />
    </>
  );
}
